import { z } from 'zod';
import { answerOutcomeSchema, conversationEventKindSchema, handoffReasonSchema, leadContactTypeSchema, markBodySchema, markKindSchema, MAX_AGENT_MESSAGE_LENGTH, messageRoleSchema, messageSourceSchema, } from '../../conversations.js';
import { usageSchema } from './billing.js';
/**
 * A website's stored conversations and one conversation in full
 * (`features/website-conversations/technical.md`, API contracts), and live
 * chat: an agent takes a chat over from the assistant for good, replies in it,
 * and can transfer it to another agent. Only the agent holding a chat replies
 * in it. Every route needs the conversations and marks permission
 * (`patterns/auth-patterns.md`, 3).
 */
/** Page-number pagination (`patterns/api-conventions.md`, 5). */
export const CONVERSATIONS_PAGE_SIZE = 50;
export const listConversationsQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(CONVERSATIONS_PAGE_SIZE),
});
export const conversationListItemSchema = z.object({
    id: z.uuid(),
    /** The full text; the dashboard truncates it to the width it has. */
    firstVisitorMessage: z.string().nullable(),
    /** Visitor and assistant messages; the automatic greeting isn't counted. */
    messageCount: z.number().int().min(0),
    hasLead: z.boolean(),
    /** The agent handling the chat, once one has taken it over; `null` while the assistant is. */
    agentName: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const listConversationsResponseSchema = z.object({
    items: z.array(conversationListItemSchema),
    /** The website's answered messages this billing cycle, for the counter above the list. */
    usage: usageSchema,
    /** The page returned, which is the last page when a later one was asked for. */
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    total: z.number().int().min(0),
});
/** Newest first, by when the conversation started. */
export const listConversations = {
    method: 'GET',
    path: '/api/dashboard/websites/:websiteId/conversations',
    params: z.object({ websiteId: z.uuid() }),
    query: listConversationsQuerySchema,
    body: z.object({}),
    response: listConversationsResponseSchema,
};
export const conversationParamsSchema = z.object({
    websiteId: z.uuid(),
    conversationId: z.uuid(),
});
/** An Account User's mark on an answer (`product/business-rules.md`, Marks and feedback). */
export const messageMarkSchema = z.object({
    id: z.uuid(),
    kind: markKindSchema,
    feedback: z.string(),
    /** Who marked it, by their name now; kept after they're removed from the account. */
    markedBy: z.object({ accountUserId: z.uuid(), name: z.string() }),
    /** The viewer's own mark, which they can change or remove. */
    mine: z.boolean(),
    createdAt: z.iso.datetime(),
});
export const conversationMessageSchema = z.object({
    id: z.uuid(),
    role: messageRoleSchema,
    /** Visitor text exactly as typed (`product/business-rules.md`, Visitors and conversations). */
    content: z.string(),
    /** Assistant messages only; `null` for visitor and greeting messages. */
    answerOutcome: answerOutcomeSchema.nullable(),
    /** Only when `answerOutcome` is `handoff`. */
    handoffReason: handoffReasonSchema.nullable(),
    /** `null` for visitor and greeting messages; empty for a custom-page answer. */
    sources: z.array(messageSourceSchema).nullable(),
    /** Agent messages only: who replied, by the name they had when they sent it. */
    author: z.object({ accountUserId: z.uuid().nullable(), name: z.string() }).nullable(),
    /** Assistant answers only: the marks Account Users made on it; empty otherwise. */
    marks: z.array(messageMarkSchema),
    createdAt: z.iso.datetime(),
});
export const conversationLeadSchema = z.object({
    contactType: leadContactTypeSchema,
    /** When the visitor first tapped the contact details or left their own. */
    createdAt: z.iso.datetime(),
    /** What the visitor left in the lead form; all `null` for a tap. */
    name: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    message: z.string().nullable(),
});
/** A change to who handles a chat, in the transcript where it happened. */
export const conversationEventSchema = z.object({
    kind: conversationEventKindSchema,
    /** Who took it over, or who transferred it. */
    byName: z.string(),
    /** For a transfer: who it went to. */
    toName: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
/** A person who can handle chats: an Account User with the conversations permission. */
export const agentSchema = z.object({
    accountUserId: z.uuid(),
    name: z.string(),
});
export const conversationResponseSchema = z.object({
    id: z.uuid(),
    createdAt: z.iso.datetime(),
    /** Oldest first, in the order they were sent. */
    messages: z.array(conversationMessageSchema),
    /** At most one per conversation (`product/business-rules.md`, Leads). */
    lead: conversationLeadSchema.nullable(),
    /** Takeovers and transfers, oldest first. */
    events: z.array(conversationEventSchema),
    /**
     * The agent handling the chat once it was taken over; `null` while the
     * assistant answers. A chat taken over never goes back to the assistant.
     */
    agent: agentSchema.nullable(),
    /** Whether the Account User asking is that agent, and so can reply. */
    isMine: z.boolean(),
    /**
     * Whether the Account User asking holds the live chat permission, and so can
     * take a chat over and reply in it (`patterns/auth-patterns.md`, 3).
     */
    canLiveChat: z.boolean(),
    /** Whether they can transfer it: the agent holding it, or a super user, with live chat. */
    canTransfer: z.boolean(),
    /** Ended, or left alone too long: no takeover and no replies. */
    ended: z.boolean(),
});
export const getConversation = {
    method: 'GET',
    path: '/api/dashboard/websites/:websiteId/conversations/:conversationId',
    params: conversationParamsSchema,
    query: z.object({}),
    body: z.object({}),
    response: conversationResponseSchema,
};
/**
 * Takes the chat over from the assistant, for good: the assistant answers no
 * more of its messages. `CONFLICT` when another agent already has it, or it
 * has ended. Taking over a chat you already hold changes nothing.
 */
export const takeOverConversation = {
    method: 'POST',
    path: '/api/dashboard/websites/:websiteId/conversations/:conversationId/takeover',
    params: conversationParamsSchema,
    query: z.object({}),
    body: z.object({}),
    response: conversationResponseSchema,
};
export const sendAgentMessageBodySchema = z.object({
    /** A retry key: the same id sent again stores the reply once. */
    clientMessageId: z.uuid(),
    content: z.string().trim().min(1, 'Write a reply.').max(MAX_AGENT_MESSAGE_LENGTH),
});
/**
 * A reply from the agent holding the chat. `CONFLICT` for anyone else, before
 * a takeover, or once the chat has ended. Agent replies are never counted as
 * answered messages for billing.
 */
export const sendAgentMessage = {
    method: 'POST',
    path: '/api/dashboard/websites/:websiteId/conversations/:conversationId/agent-messages',
    params: conversationParamsSchema,
    query: z.object({}),
    body: sendAgentMessageBodySchema,
    response: conversationResponseSchema,
};
export const transferConversationBodySchema = z.object({ accountUserId: z.uuid() });
/**
 * Hands the chat to another agent, who then holds it alone. Only the agent
 * holding it, or a super user, can transfer it.
 */
export const transferConversation = {
    method: 'POST',
    path: '/api/dashboard/websites/:websiteId/conversations/:conversationId/transfer',
    params: conversationParamsSchema,
    query: z.object({}),
    body: transferConversationBodySchema,
    response: conversationResponseSchema,
};
/** Who a chat can be transferred to: every agent in the account, by name. */
export const listAgents = {
    method: 'GET',
    path: '/api/dashboard/websites/:websiteId/agents',
    params: z.object({ websiteId: z.uuid() }),
    query: z.object({}),
    body: z.object({}),
    response: z.object({ items: z.array(agentSchema) }),
};
/**
 * Ends a live chat for good: the visitor is told it ended, and their next
 * question starts a new conversation with the assistant. Only the agent
 * holding it, or a super user, can end it. Ending a chat that already ended
 * changes nothing.
 */
export const endLiveChat = {
    method: 'POST',
    path: '/api/dashboard/websites/:websiteId/conversations/:conversationId/end',
    params: conversationParamsSchema,
    query: z.object({}),
    body: z.object({}),
    response: conversationResponseSchema,
};
/** One live chat the Account User is handling, for the chat windows and alerts. */
export const liveChatSummarySchema = z.object({
    websiteId: z.uuid(),
    websiteName: z.string(),
    conversationId: z.uuid(),
    /** The visitor's first question, or `null` before they asked one. */
    firstVisitorMessage: z.string().nullable(),
    /** The newest message from anyone; `null` with none. */
    lastMessage: z
        .object({
        role: messageRoleSchema,
        /** Cut to a preview's length. */
        content: z.string(),
        createdAt: z.iso.datetime(),
    })
        .nullable(),
    /** When the visitor last wrote; `null` if they never did. */
    lastVisitorMessageAt: z.iso.datetime().nullable(),
    /** The visitor wrote last, so the chat is waiting for the agent. */
    awaitingReply: z.boolean(),
    takenOverAt: z.iso.datetime(),
});
/** How long a live chat's message preview is, at most. */
export const LIVE_CHAT_PREVIEW_LENGTH = 140;
/**
 * Every live chat the Account User is handling now, across the account's
 * websites, newest visitor message first. Refetched on every conversation
 * event (`streamConversationEvents`), and polled while the stream is down, so
 * an agent hears about a visitor's message on any page. Needs the live chat
 * permission.
 */
export const listMyLiveChats = {
    method: 'GET',
    path: '/api/dashboard/live-chats',
    params: z.object({}),
    query: z.object({}),
    body: z.object({}),
    response: z.object({ items: z.array(liveChatSummarySchema) }),
};
// ---------------------------------------------------------------------------
// Conversation events (server-sent events)
// ---------------------------------------------------------------------------
/**
 * A stream of `text/event-stream` events, not JSON: every conversation that
 * changes in the Account User's account (a message, a take-over, a transfer,
 * an end, a lead), so the dashboard refetches what it shows instead of
 * polling. An event only names the conversation; what changed is read through
 * the usual endpoints, with their permissions. `resync` means events may have
 * been missed: refetch everything. The server ends the stream every so often
 * and the browser reconnects, so the session is checked again.
 * Needs the conversations and marks permission.
 */
export const streamConversationEvents = {
    method: 'GET',
    path: '/api/dashboard/conversation-events',
    params: z.object({}),
    query: z.object({}),
    body: z.object({}),
    response: z.never(),
};
/** The `data` of a `conversation` event. */
export const conversationChangedEventSchema = z.object({
    websiteId: z.uuid(),
    conversationId: z.uuid(),
});
// ---------------------------------------------------------------------------
// Leads
// ---------------------------------------------------------------------------
export const leadListItemSchema = conversationLeadSchema.extend({
    id: z.uuid(),
    conversationId: z.uuid(),
    /** The conversation's first visitor message, for context. */
    firstVisitorMessage: z.string().nullable(),
});
export const listLeadsResponseSchema = z.object({
    items: z.array(leadListItemSchema),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    total: z.number().int().min(0),
});
/**
 * A website's leads, newest first: visitors who left their details and those
 * who tapped a contact route in the chat. Needs the conversations permission,
 * as the conversations they come from do.
 */
export const listLeads = {
    method: 'GET',
    path: '/api/dashboard/websites/:websiteId/leads',
    params: z.object({ websiteId: z.uuid() }),
    query: listConversationsQuerySchema,
    body: z.object({}),
    response: listLeadsResponseSchema,
};
// ---------------------------------------------------------------------------
// Marks: an answer flagged as invalid or incorrect, for Groundly to review
// ---------------------------------------------------------------------------
export const markParamsSchema = conversationParamsSchema.extend({ messageId: z.uuid() });
/**
 * Marks one of the assistant's answers as invalid or incorrect, with the
 * required feedback, or changes the viewer's own mark on it. Needs the
 * conversations and marks permission. Answers the conversation again.
 */
export const markAnswer = {
    method: 'POST',
    path: '/api/dashboard/websites/:websiteId/conversations/:conversationId/messages/:messageId/mark',
    params: markParamsSchema,
    query: z.object({}),
    body: markBodySchema,
    response: conversationResponseSchema,
};
/** Removes the viewer's own mark from an answer. Answers the conversation again. */
export const unmarkAnswer = {
    method: 'DELETE',
    path: '/api/dashboard/websites/:websiteId/conversations/:conversationId/messages/:messageId/mark',
    params: markParamsSchema,
    query: z.object({}),
    body: z.object({}),
    response: conversationResponseSchema,
};
//# sourceMappingURL=conversations.js.map